import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Camera } from "../../api/cameras.ts";
import { fetchMyCameraAccess, requestCameraAccess } from "../../api/cameraAccess.ts";
import { ApiRequestError } from "../../api/client.ts";
import { getLastCameraId } from "../../lib/dashboardSession.ts";
import { useAuth } from "../../lib/place360Auth.tsx";
import { DualCameraPreview } from "./DualCameraPreview.tsx";
import { CamerasEmptyPanel } from "./CamerasEmptyPanel.tsx";

export type LivePreviewPaneProps = {
  cameras: Camera[];
  previewCameraId: string | null;
  onSelectCamera: (id: string) => void;
  loadError: string | null;
  onRetry: () => void;
  toolbarTrailing?: ReactNode;
};

function pickCamera(
  cameras: Camera[],
  previewCameraId: string | null,
  lastId: string | null,
): Camera | null {
  if (previewCameraId) {
    const hit = cameras.find((c) => c.id === previewCameraId);
    if (hit) return hit;
  }
  if (lastId) {
    const hit = cameras.find((c) => c.id === lastId);
    if (hit) return hit;
  }
  return cameras[0] ?? null;
}

export function LivePreviewPane({
  cameras,
  previewCameraId,
  onSelectCamera,
  loadError,
  onRetry,
  toolbarTrailing,
}: LivePreviewPaneProps) {
  const { user } = useAuth();
  const controlsHostRef = useRef<HTMLDivElement>(null);
  const [accessReloadTick, setAccessReloadTick] = useState(0);
  const [liveAccess, setLiveAccess] = useState<{
    loaded: boolean;
    status: "none" | "pending" | "active" | "blocked";
    ptzAllowed: boolean;
    blockedUntil: string | null;
  }>({ loaded: false, status: "none", ptzAllowed: false, blockedUntil: null });
  const [requestBusy, setRequestBusy] = useState(false);

  useEffect(() => {
    if (loadError || cameras.length === 0) return;
    const next = pickCamera(cameras, previewCameraId, getLastCameraId());
    if (!next) return;
    if (next.id !== previewCameraId) {
      onSelectCamera(next.id);
    }
  }, [cameras, previewCameraId, loadError, onSelectCamera]);

  const active = useMemo(
    () => pickCamera(cameras, previewCameraId, getLastCameraId()),
    [cameras, previewCameraId],
  );

  useEffect(() => {
    if (!active?.id) return;
    if (user?.role === "admin") {
      setLiveAccess({
        loaded: true,
        status: "active",
        ptzAllowed: true,
        blockedUntil: null,
      });
      return;
    }
    if (!user?.id) {
      setLiveAccess({
        loaded: false,
        status: "none",
        ptzAllowed: false,
        blockedUntil: null,
      });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const r = await fetchMyCameraAccess(active.id);
        if (cancelled) return;
        setLiveAccess({
          loaded: true,
          status: r.status,
          ptzAllowed: r.ptzAllowed,
          blockedUntil: r.blockedUntil,
        });
      } catch {
        if (cancelled) return;
        setLiveAccess({
          loaded: true,
          status: "none",
          ptzAllowed: false,
          blockedUntil: null,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active?.id, user?.id, user?.role, accessReloadTick]);

  const onRequestAccess = useCallback(async () => {
    if (!active?.id || user?.role === "admin") return;
    setRequestBusy(true);
    try {
      await requestCameraAccess(active.id);
      setAccessReloadTick((n) => n + 1);
    } catch (e) {
      const msg =
        e instanceof ApiRequestError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Could not submit request.";
      window.alert(msg);
    } finally {
      setRequestBusy(false);
    }
  }, [active?.id, user?.role]);

  if (loadError || cameras.length === 0) {
    return (
      <CamerasEmptyPanel
        variant="liveView"
        reason={loadError ? "error" : "empty"}
        onRetry={loadError ? onRetry : undefined}
        errorDetail={loadError}
      />
    );
  }

  const showRequestRow =
    user?.role === "user" && active && liveAccess.loaded && !liveAccess.ptzAllowed;

  return (
    <div className="ds-stack ds-stack--loose ds-live-preview">
      <div className="ds-live-preview__header">
        <label className="ds-text-secondary ds-live-preview__camera">
          <span className="ds-live-preview__camera-label">Camera</span>
          <select
            className="ds-cam-filter-select ds-live-preview__select"
            value={active?.id ?? ""}
            onChange={(e) => onSelectCamera(e.target.value)}
          >
            {cameras.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        {showRequestRow ? (
          <div
            className="ds-live-preview__access"
            style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}
          >
            {liveAccess.status === "none" || liveAccess.status === "blocked" ? (
              <>
                <span className="ds-text-secondary" style={{ fontSize: 13 }}>
                  {liveAccess.status === "blocked"
                    ? liveAccess.blockedUntil
                      ? `PTZ is blocked until ${new Date(liveAccess.blockedUntil).toLocaleString()} (or sooner if an admin acts). You can send a new access request for review.`
                      : "PTZ is blocked on this camera. You can send a new access request for an admin to review."
                    : "PTZ requires admin approval for this camera."}
                </span>
                <button
                  type="button"
                  className="ds-btn ds-btn--secondary"
                  disabled={requestBusy}
                  onClick={() => void onRequestAccess()}
                >
                  {requestBusy ? "Sending…" : "Request access"}
                </button>
              </>
            ) : null}
            {liveAccess.status === "pending" ? (
              <span className="ds-text-secondary" style={{ fontSize: 13 }}>
                Access request sent — an admin can approve it when they review camera access.
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="ds-live-preview__controls-cluster">
          <div className="ds-live-preview__controls" ref={controlsHostRef} />
          {toolbarTrailing}
        </div>
      </div>
      {active ? (
        <DualCameraPreview
          camera={active}
          controlsHostRef={controlsHostRef}
          accessReloadTick={accessReloadTick}
        />
      ) : null}
    </div>
  );
}
